import { useEffect, useRef, useState } from "react";
import { EventsType } from "../Common/Types/types";
import { Button } from "@progress/kendo-react-buttons";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridFilterCellProps,
} from "@progress/kendo-react-grid";
import moment from "moment";
import { DropdownFilterCell } from "../Common/Components/DropDownFilterCell";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import {
  Menu,
  MenuItem,
  MenuItemLink,
  MenuSelectEvent,
} from "@progress/kendo-react-layout";

interface EventsGridProps {
  data: EventsType[] | null;
  updateIdsHandler: any;
}
const EventsGrid = (props: EventsGridProps) => {
  const initialSort: any = [
    {
      field: "eventID",
      dir: "desc",
    },
  ];
  const initialDataState: { skip: number; take: number } = {
    skip: 0,
    take: 100,
  };
  const [data, setData] = useState<EventsType[]>([]);
  const [sort, setSort] = useState(initialSort);
  const [filter, setFilter] = useState();
  const [page, setPage] = useState<{ skip: number; take: number }>(
    initialDataState
  );

  useEffect(() => {
    if (props.data != null) {
      setData(props.data);
      if (props.data.length < initialDataState.take)
        setPage({ skip: 0, take: props.data.length });
    }
  }, [props.data]);

  const filterChange = (event: any) => {
    if (props.data != null) {
      setData(filterBy(props.data, event.filter));
      setFilter(event.filter);
    }
  };
  const pageChange = (event: any) => {
    setPage(event.page);
  };
  const _export = useRef<ExcelExport | null>(null);
  const excelExport = (event: MenuSelectEvent) => {
    if (event.item.text === "Export to Excel") {
      if (_export.current !== null) {
        _export.current.save();
      }
    }
  };
  function renderMenuIconIfApplicable(props: any) {
    return props.item.icon ? (
      <span
        style={{ fontSize: "16px" }}
        className={`k-icon k-i-${props.item.icon}`}
        role="presentation"
        key="0"
      />
    ) : null;
  }
  const linkRender = (props: any) => {
    return (
      <MenuItemLink url={props.item.url} opened={props.opened}>
        {[renderMenuIconIfApplicable(props), props.item.text, null]}
      </MenuItemLink>
    );
  };
  if (data == null) {
    return null; //<div>No data found for selected filters</div>;
  }

  const customModifiedDateCell = (props: GridCellProps) => {
    if (props.dataItem.modified !== "") {
      return <td>{moment(props.dataItem.modified).format("DD.MM.YYYY")}</td>;
    }
    return <td>{props.dataItem.modified}</td>;
  };
  const customCreatedDateCell = (props: GridCellProps) => {
    if (props.dataItem.created !== "") {
      return <td>{moment(props.dataItem.created).format("DD.MM.YYYY")}</td>;
    }
    return <td>{props.dataItem.created}</td>;
  };
  const customEventDateCell = (props: GridCellProps) => {
    if (props.dataItem.startDate !== "") {
      return <td>{moment(props.dataItem.startDate).format("DD.MM.YYYY")}</td>;
    }
    return <td>{props.dataItem.startDate}</td>;
  };
  const customAttachmentCell = (props: GridCellProps) => {
    const value = props.dataItem.hasAttachments;
    if (value === true) {
      return (
        <td>
          <span
            className="k-icon k-i-check-circle"
            style={{ color: "green" }}
          ></span>
        </td>
      );
    } else if (value === false) {
      return (
        <td>
          <span className="k-icon k-i-x-circle" style={{ color: "red" }}></span>
        </td>
      );
    }
  };

  const eventTypeFilterCell: any = (props: GridFilterCellProps) => (
    <DropdownFilterCell
      {...props}
      data={["Service Report", "Jira Issue"]}
      defaultItem={"All"}
    />
  );
  const eventStatusFilterCell: any = (props: GridFilterCellProps) => (
    <DropdownFilterCell
      {...props}
      data={["In Progress", "Finished", "Sent", "Closed"]}
      defaultItem={"All"}
    />
  );
  const viewReport: any = (cellprops: GridFilterCellProps) => {
    return (
      <td>
        <Button
          onClick={() =>
            props.updateIdsHandler({
              eventId: cellprops.dataItem.eventID,
              customerId: cellprops.dataItem.customerID,
            })
          }
        >
          Download
        </Button>
      </td>
    );
  };

  return (
    <div style={{ height: "100%", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: "-20px",
          left: "-20px",
        }}
      >
        <Menu openOnClick={true} onSelect={excelExport} linkRender={linkRender}>
          <MenuItem
            text=""
            icon="more-horizontal"
            data={{}}
            cssStyle={{ color: "black" }}
          >
            <MenuItem text="Export to Excel" />
          </MenuItem>
        </Menu>
      </div>
      <ExcelExport data={data} ref={_export}>
        <Grid
          data={orderBy(data, sort).slice(page.skip, page.take + page.skip)}
          style={{ height: "100%" }}
          sortable={true}
          skip={page.skip}
          take={page.take}
          pageSize={20}
          total={data.length}
          sort={sort}
          onSortChange={(e) => {
            setSort(e.sort);
          }}
          filterable={true}
          filter={filter}
          scrollable={"virtual"}
          onFilterChange={filterChange}
          onPageChange={pageChange}
          resizable={true}
        >
          <GridColumn
            field="updatedReportStatus"
            title="Event Status"
            width="150px"
            filterCell={eventStatusFilterCell}
          />
          <GridColumn field="country" title="Country" width="150px" />
          <GridColumn
            field="customerName"
            title="Customer Name"
            width="150px"
          />
          <GridColumn field="machineSN" title="Machine SN" width="150px" />
          <GridColumn field="svcCallId" title="SCL/SCM Number" width="150px" />
          {/*           <GridColumn field="svcStatus" title="SCL Status" width="150px" /> */}

          <GridColumn field="faultDescription" title="Subject" width="150px" />
          <GridColumn
            field="servicePersonnel"
            title="Personnel"
            width="150px"
          />
          <GridColumn field="engineer" title="Creator" width="150px" />
          <GridColumn
            field="created"
            title="Created"
            filter="date"
            width="150px"
            cell={customCreatedDateCell}
          />
          <GridColumn
            field="modified"
            title="Modified"
            filter="date"
            width="150px"
            cell={customModifiedDateCell}
          />
          {/*  <GridColumn
            field="startDate"
            title="Event Date"
            filter="date"
            width="150px"
            cell={customEventDateCell}
          /> */}
          <GridColumn field="workHours" title="Hours" width="120px" />
          <GridColumn
            field="hasAttachments"
            title="Has Attachments"
            width="150px"
            filter="boolean"
            cell={customAttachmentCell}
          />
          <GridColumn
            field="workHours"
            title="Download Report"
            cell={viewReport}
            width="120px"
          />
        </Grid>
      </ExcelExport>
    </div>
  );
};
export default EventsGrid;
